import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getBenchmarks,
  getBenchmarkOptOut,
  updateBenchmarkOptOut,
} from '../services/api';
import type { BenchmarkComparison } from '../types';

/**
 * Phase 3 — F3.2: Competitive Benchmarking hooks.
 * TanStack React Query wrappers for the benchmarking API.
 */

/** Fetch benchmark comparison for the current store. */
export function useBenchmarks() {
  return useQuery<BenchmarkComparison>({
    queryKey: ['benchmarks'],
    queryFn: () => getBenchmarks(),
    staleTime: 5 * 60_000, // 5 minutes — benchmark data changes nightly
    retry: false,
  });
}

/** Fetch opt-out preference for benchmark data contribution. */
export function useBenchmarkOptOut() {
  return useQuery<{ optedOut: boolean }>({
    queryKey: ['benchmark-opt-out'],
    queryFn: () => getBenchmarkOptOut(),
    staleTime: 60_000,
  });
}

/** Toggle benchmark opt-out. Invalidates benchmark queries. */
export function useUpdateBenchmarkOptOut() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, boolean>({
    mutationFn: (optOut) => updateBenchmarkOptOut(optOut),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-opt-out'] });
      queryClient.invalidateQueries({ queryKey: ['benchmarks'] });
    },
  });
}
